import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { JWTDecoded } from "./useIhrApi";
import { IStripeSubInfoResponse } from "./apiResponseTypes";

///////// REMEMBER - these must be freed when logging out!!!
// TODO enumerate - use a forEach to free local store - throw warning if array freed doesn't match key number
const TOKEN_KEY = "auth_token";
const EMAIL_KEY = "user_email";
const IS_PREMIUM_KEY = "user_is_premium";
const PREMIUM_FROM_STRIPE_KEY = "premium_from_stripe";
const ACTIVE_STRIPE_SUBSCRIPTION_KEY = "active_stripe_subscription_key";
// DO NOT ADD MORE KEYS WITHOUT VERIFYING THAT THEY ARE FREED ON LOGOUT!!

// const LOCAL_STORAGE_KEYS = {
//     TOKEN_KEY : "auth_token",
//     EMAIL_KEY : "user_email",
//     IS_PREMIUM_KEY : "user_is_premium",
//     PREMIUM_FROM_STRIPE_KEY : "premium_from_stripe",
//     ACTIVE_STRIPE_SUBSCRIPTION_KEY : "active_stripe_subscription_key",
// }

// export interface SubscriptionEligibility {
//     eligible: boolean;
//     reason?: string;
//     [key: string]: unknown;
// }

export function useAuthToken() {
    const [token, setToken] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [isPremium, setIsPremium] = useState<boolean | null>(null);
    const [premiumFromStripe, setPremiumFromStripe] = useState<boolean | null>(
        null,
    );
    const [activeStripeSub, setActiveStripeSub] =
        useState<IStripeSubInfoResponse | null>(null);
    const [expiresAt, setExpiresAt] = useState<number | null>(null);

    // NEW: eligibility state - deprecated
    //const [eligibility, setEligibility] = useState<boolean>(false);

    // ensures that the token call has been made so that protected routes render properly
    const [isAuthResolved, setIsAuthResolved] = useState(false);

    const isSignedIn = token !== null;

    const saveToken = (newToken: string) => {
        try {
            const payload = jwtDecode<JWTDecoded>(newToken);
            localStorage.setItem(TOKEN_KEY, newToken);
            setToken(newToken);
            setExpiresAt(payload.exp * 1000);
        } catch (e) {
            console.error("Failed to decode and save token", e);
        }
    };

    const saveEmail = (userEmail: string) => {
        try {
            localStorage.setItem(EMAIL_KEY, userEmail);
            setEmail(userEmail);
        } catch (e) {
            console.error("Failed to save email", e);
        }
    };

    const saveIsPremium = (userIsPremium: boolean) => {
        try {
            localStorage.setItem(IS_PREMIUM_KEY, userIsPremium ? "1" : "0");
            setIsPremium(userIsPremium);
        } catch (e) {
            console.error("Failed to save user is premium", e);
        }
    };

    const savePremiumFromStripe = (premFromStripe: boolean) => {
        try {
            localStorage.setItem(
                PREMIUM_FROM_STRIPE_KEY,
                premFromStripe ? "1" : "0",
            );
            setPremiumFromStripe(premFromStripe);
        } catch (e) {
            console.error("Failed to save user prem from stripe", e);
        }
    };

    const saveActiveSubscription = (activeSub: IStripeSubInfoResponse) => {
        try {
            const activeStripeSub = JSON.stringify(activeSub);
            localStorage.setItem(
                ACTIVE_STRIPE_SUBSCRIPTION_KEY,
                activeStripeSub,
            );
            setActiveStripeSub(activeSub);
        } catch (e) {
            console.error("Failed to save user is premium", e);
        }
    };

    const clearLocalSubscriptionStorage = () => {
        localStorage.removeItem(IS_PREMIUM_KEY);
        localStorage.removeItem(PREMIUM_FROM_STRIPE_KEY);
        localStorage.removeItem(ACTIVE_STRIPE_SUBSCRIPTION_KEY);
        setIsPremium(null);
        setPremiumFromStripe(null);
        setActiveStripeSub(null);
    };

    const logout = () => {
        clearLocalSubscriptionStorage();
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EMAIL_KEY);
        setToken(null);
        setExpiresAt(null);
        setEmail(null);
        //setEligibility(false); // clear eligibility on logout.. I dint think this is relevant
    };

    useEffect(() => {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedEmail = localStorage.getItem(EMAIL_KEY);
        const premFromStripeQuery = localStorage.getItem(
            PREMIUM_FROM_STRIPE_KEY,
        ); //=== "1";
        const storedUserIsPremiumQuery = localStorage.getItem(IS_PREMIUM_KEY); //=== "1";

        let premFromStripe: null | boolean = null;
        let storedUserIsPremium: null | boolean = null;
        if (premFromStripeQuery === "1") {
            premFromStripe = true;
        } else if (premFromStripeQuery === "2") {
            premFromStripe = false;
        }

        if (storedUserIsPremiumQuery === "1") {
            storedUserIsPremium = true;
        } else if (storedUserIsPremiumQuery === "2") {
            storedUserIsPremium = false;
        }

        const activeStripeSubscription = localStorage.getItem(
            ACTIVE_STRIPE_SUBSCRIPTION_KEY,
        ) as string | null;

        // setToken(storedToken); ----- this is set after verifying the token can be decoded
        setEmail(storedEmail);
        setIsPremium(storedUserIsPremium);
        setPremiumFromStripe(premFromStripe);

        if (storedToken) {
            try {
                const payload = jwtDecode<JWTDecoded>(storedToken);
                const expires = payload.exp * 1000;
                if (expires > Date.now()) {
                    setToken(storedToken);
                    setExpiresAt(expires);
                } else {
                    localStorage.removeItem(TOKEN_KEY);
                }
            } catch (error) {
                console.error("Invalid token in storage", error);
                localStorage.removeItem(TOKEN_KEY);
            }
        }

        if (!!activeStripeSubscription) {
            const parsedActiveStripeSub = JSON.parse(
                activeStripeSubscription,
            ) as IStripeSubInfoResponse;
            setActiveStripeSub(parsedActiveStripeSub);
        }

        setIsAuthResolved(true);
    }, []);

    useEffect(() => {
        if (!expiresAt) return;
        const timeLeft = expiresAt - Date.now();
        if (timeLeft <= 0) {
            logout();
            return;
        }
        const timeout = setTimeout(logout, timeLeft);
        return () => clearTimeout(timeout);
    }, [expiresAt]);

    return {
        token,
        isSignedIn,
        isAuthResolved,
        email,
        isPremium,
        premiumFromStripe,
        expiresAt,
        activeStripeSub,
        // eligibility,
        // fetchEligibility,
        saveIsPremium,
        savePremiumFromStripe,
        saveActiveSubscription,
        saveToken,
        saveEmail,
        clearLocalSubscriptionStorage,
        logout,
    };
}
